<script lang="ts" setup>
import { computed, ImgHTMLAttributes } from 'vue'
import { useHead } from '#imports'
import { MediaImageFragment } from '~/api/graphql/generated'
import { buildResponsiveImageSlices } from '~/lib/utils'
import type { ResponsiveImageSlice } from '~/lib/utils'
import type { Link } from '@unhead/vue'

type Size = [number | null, string][]

const imageSizes = {
  teaser: [
    [768, '100vw'],
    [1024, '50vw'],
    [null, '520px'],
  ] as Size,
  hero: [
    [1800, '100vw'],
    [null, '1800px'],
  ] as Size,
}

type ImageSizes = keyof typeof imageSizes

type FetchPriority = 'high' | 'low' | 'auto'

const getSizes = (type: ImageSizes) =>
  imageSizes[type]
    .reduce((acc, item) => {
      if (!item[0]) return `${acc}${item[1]}, `
      return `${acc}(max-width: ${item[0] - 1}px) ${item[1]}, `
    }, '')
    .slice(0, -2)

const getSrcset = (data: ResponsiveImageSlice[]) =>
  data
    .reduce((acc: string, slice: ResponsiveImageSlice): string => `${acc}${slice.url} ${slice.width}w,`, '')
    .slice(0, -1)

const props = withDefaults(
  defineProps<{
    small?: MediaImageFragment
    large?: MediaImageFragment
    type?: ImageSizes
    loading?: 'lazy' | 'eager'
    fetchpriority?: FetchPriority
    preload?: boolean
  }>(),
  {
    type: 'teaser',
    small: undefined,
    large: undefined,
    loading: 'lazy',
    fetchpriority: 'auto',
    preload: false,
  },
)

type ImageData = MediaImageFragment['fieldMediaImage']

const getAttrs = (imageData: ImageData | null | undefined) => {
  if (!imageData) return undefined

  const slices = buildResponsiveImageSlices(imageData)

  const primarySlice = slices[0]
  if (!primarySlice) return undefined
  const width = imageData.width ?? primarySlice.width
  const height = imageData.height ?? primarySlice.height

  const attrs = {
    width,
    height,
    src: primarySlice.url,
    srcset: getSrcset(slices),
    sizes: getSizes(props.type),
    alt: imageData?.alt || '',
    loading: props.loading,
  } as ImgHTMLAttributes & { fetchpriority?: FetchPriority }

  if (props.fetchpriority && props.fetchpriority !== 'auto') {
    attrs.fetchpriority = props.fetchpriority
  }

  return attrs
}

const image = computed(() => ({
  small: getAttrs(props.small?.fieldMediaImage),
  large: getAttrs(props.large?.fieldMediaImage),
}))

useHead(() => {
  if (!props.preload) return {}
  const data = image.value
  const candidate = data.large ?? data.small
  if (!candidate?.src) return {}

  const link: Link = {
    key: `preload-${candidate.src}`,
    rel: 'preload',
    as: 'image',
    href: candidate.src,
  }

  if (props.fetchpriority && props.fetchpriority !== 'auto') {
    link.fetchpriority = props.fetchpriority
  }

  const srcset = data.large?.srcset || data.small?.srcset
  if (srcset) link.imagesrcset = srcset
  const sizes = data.large?.sizes || data.small?.sizes
  if (sizes) link.imagesizes = sizes

  return {
    link: [link],
  }
})
</script>
<template>
  <picture v-if="image.small || image.large" class="dp-picture">
    <source
      v-if="image.large"
      :width="image.large.width"
      :height="image.large.height"
      :srcset="image.large.srcset"
      :sizes="image.large.sizes"
      media="(min-width: 768px)"
    />
    <img v-bind="image.small ?? image.large" />
  </picture>
</template>

<style scoped lang="scss">
img {
  object-fit: cover;
}
</style>
